import { useState } from 'react';

import type Paginated from 'app/core/paginable';
import { useGetApplication } from 'app/hooks';
import httpApiApplication from 'app/http/httpApiApplication';
import { catalogs } from 'app/modules/Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from 'app/modules/Parameters/Catalog/Catalog/services/useCatalog';
import { type Taxpayer } from 'app/modules/Reports/TaxReturnList/types';

import { type Tax, type TaxPayerTax } from '../models/Tax';
import { type TaxPeriodicity } from '../models/Tax';
import {
  BaseUrlTax,
  BaseUrlTaxpayer,
  BaseUrlTaxPeriodicity,
  BaseUrlTaxRates
} from '../services/tax.service';

export const useTax = (id: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Paginated<Tax>>({
    url: BaseUrlTax,
    params: {
      id,
      page: 1,
      pageSize: 1
    }
  });

  const [tax] = data?.data || [];

  return { loading, data: tax, error, response };
};

export const useGetTaxpayer = (id: string) => {
  const [state] = useState({});
  const [{ data, loading, error, response }] = useGetApplication<Taxpayer>({
    url: `${BaseUrlTaxpayer}/${id}`,
    params: state
  });

  return { loading, data, error, response };
};

export const useTaxRate = (taxId: string, id: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Paginated<Tax>>({
    url: `${BaseUrlTax}/${taxId}${BaseUrlTaxRates}`,
    params: {
      id,
      page: 1,
      pageSize: 1
    }
  });

  const [tax] = data?.data || [];

  return { loading, data: tax, error, response };
};

export const useTaxPeriodicity = (taxId: string, id: string) => {
  const [{ data, loading, error, response }] = useGetApplication<Paginated<TaxPeriodicity>>({
    url: `${BaseUrlTax}/${taxId}${BaseUrlTaxPeriodicity}`,
    params: {
      id,
      page: 1,
      pageSize: 1
    }
  });

  const [taxPeriodicity] = data?.data || [];

  return { loading, data: taxPeriodicity, error, response };
};

export const usePeriodicityTypes = () => {
  return useCatalogList(catalogs.periodicityType);
};

export const useRateTypes = () => {
  return useCatalogList(catalogs.rateType);
};

export const useGetAllTaxes = (loadMode = true) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<Tax>>(
    {
      url: `${BaseUrlTax}/all`,
      params: undefined
    },
    loadMode
  );

  return { loading, data: data, error, response };
};

export const useGetAllTaxesWitCurrentLang = (loadMode = true) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<Tax>>(
    {
      url: `${BaseUrlTax}/all`,
      params: undefined,
      headers: {
        lang: true
      }
    },
    loadMode
  );

  return { loading, data: data, error, response };
};

export const useGetAllTaxesTax = (loadMode = true) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<TaxPayerTax>>(
    {
      url: `${BaseUrlTaxpayer}/tax`,
      params: undefined
    },
    loadMode
  );

  return { loading, data: data, error, response };
};

export const useGetTaxesTaxByNit = (tin: string, loadMode = true) => {
  const [{ data, loading, error, response }] = useGetApplication<Array<TaxPayerTax>>(
    {
      url: `${BaseUrlTaxpayer}/${tin}/tax`,
      params: undefined
    },
    loadMode
  );

  return { loading, data: data, error, response };
};

export const GetAllTaxesFrom: (tin: string) => Promise<Array<TaxPayerTax>> = async (
  tin: string
) => {
  return await httpApiApplication.get<Array<TaxPayerTax>>(`${BaseUrlTaxpayer}/${tin}/tax`);
};
